import type { DocSection } from "./types";

/**
 * User-facing documentation information architecture.
 *
 * Pages follow durable product systems and the flows that connect them. Keep
 * examples inside the owning feature guide; do not create a new page for each
 * question the Agent can answer or each implementation detail behind it.
 *
 * Page order inside a section is reading order. Slugs are permanent; retired
 * slugs redirect to their closest canonical guide in worker/index.js.
 */
export const DOC_SECTIONS: readonly DocSection[] = [
  {
    id: "start",
    label: "Get started",
    blurb: "Understand the product, finish setup, and complete a first useful task.",
    pages: ["index", "getting-started", "desktop"],
  },
  {
    id: "agent-research",
    label: "Agent and research",
    blurb: "Work with the hosted Agent and move from market context to sourced research.",
    pages: ["agent", "markets", "data"],
  },
  {
    id: "strategies",
    label: "Strategies",
    blurb: "Create, test, version, and supervise executable trading strategies.",
    pages: ["strategies", "backtesting", "deployments"],
  },
  {
    id: "portfolio-trading",
    label: "Portfolio and trading",
    blurb: "Manage accounts and holdings, then review each trading decision and its history.",
    pages: ["accounts", "portfolio", "trading", "activity"],
  },
  {
    id: "automation",
    label: "Automation and connections",
    blurb: "Schedule Agent work, extend its capabilities, and connect other AI clients.",
    pages: ["automations", "skills-and-files", "mcp"],
  },
  {
    id: "account",
    label: "Account",
    blurb: "Manage your profile, plan, connections, privacy, and support options.",
    pages: ["account"],
  },
] as const;

/** Flat reading order across every section. Drives the previous/next pager. */
export const DOC_SLUGS: readonly string[] = DOC_SECTIONS.flatMap((section) => section.pages);
